import { useMemo, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { cn } from '@/services/utils';

import {
    effectiveRoleNames,
    isAssignableRole,
    isSystemRolesVisible,
    systemRoleNames,
    userSalutation,
} from '../user-management.format';
import {
    refreshUserManagementLists,
    useRestrictionFilters,
    useRoleDefinitions,
    useRoleNames,
    useUpdateUser,
    useUserGroups,
} from '../user-management.queries';
import { UserAccount } from '../user-management.types';
import { filterOptionLabel, FormField, optionValues, PrefixRankedCombobox, restrictionFilterOption, roleFilterOption, SelectOption } from './FormField';
import { UserAvatar } from './UserAvatar';

interface EditUserDialogProps {
    open: boolean;
    user: UserAccount;
    onClose: () => void;
}

export function EditUserDialog({ open, user, onClose }: EditUserDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { organization } = useOrganization();
    const rolesQuery = useRoleNames();
    const definitionsQuery = useRoleDefinitions();
    const groupsQuery = useUserGroups();
    const filtersQuery = useRestrictionFilters();
    const updateUser = useUpdateUser();

    const [roles, setRoles] = useState<string[]>(user.roles ?? []);
    const [restrictionFilter, setRestrictionFilter] = useState(user.restrictionFilter ?? '');
    const [restrictionQuery, setRestrictionQuery] = useState(user.restrictionQuery ?? '');
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
        () => (user.groupIds ?? []).map((id) => String(id)),
    );

    const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
    const selectedGroups = groups.filter((group) => selectedGroupIds.includes(String(group.id)));
    const showSystemRoles = isSystemRolesVisible(
        getRootScope(getInjector())?.systemSettings?.UI_SHOW_SYSTEM_ROLES,
    );
    const systemRoles = systemRoleNames(definitionsQuery.data ?? {}, organization, showSystemRoles);
    const effective = effectiveRoleNames(selectedGroups, roles, systemRoles);

    const roleOptions = useMemo<SelectOption[]>(
        () => (rolesQuery.data ?? []).filter(isAssignableRole).map((role) => ({ value: role, label: role })),
        [rolesQuery.data],
    );
    const groupOptions = useMemo<SelectOption[]>(
        () => groups.map((group) => ({ value: String(group.id), label: group.groupName })),
        [groups],
    );
    const filterOptions = useMemo<SelectOption[]>(() => {
        const loaded = (filtersQuery.data ?? []).filter((filter) => filter.name !== '');
        return [
            { value: '', label: t('MAKE_SELECTION') },
            ...loaded.map((filter) => ({ value: filter.name, label: filter.label })),
        ];
    }, [filtersQuery.data, t]);

    const selectedRoleOptions = roles.map((role) => roleOptions.find((option) => option.value === role) ?? { value: role, label: role });
    const selectedGroupOptions = groupOptions.filter((option) => selectedGroupIds.includes(option.value));
    const selectedFilter = filterOptions.find((option) => option.value === restrictionFilter) ?? null;
    const salutation = userSalutation(user);

    const save = () => {
        if (updateUser.isPending) {
            return;
        }
        updateUser.mutate({
            accountId: user.id,
            restrictionQuery,
            restrictionFilter,
            groupIds: groupsQuery.isSuccess
                ? selectedGroups.map((group) => group.id)
                : (user.groupIds ?? []),
            roles,
        }, {
            onSuccess: () => {
                toast.success('USER_MANAGEMENT.USER_UPDATE_SUCCESS');
                refreshUserManagementLists();
                onClose();
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
            <DialogContent variant="small" className="modal-dialog">
                <DialogHeader>
                    <DialogHeaderDefault>{t('USER_MANAGEMENT.EDIT_USER')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogDescription className="tw-sr-only">{t('USER_MANAGEMENT.EDIT_USER')}</DialogDescription>
                <DialogBody className="tw-flex tw-max-h-[70vh] tw-flex-col tw-gap-6 tw-overflow-auto">
                    <div id="user-information" className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
                        <UserAvatar imageUrl={user.imageUrl} name={user.displayName || salutation} size="dialog" />
                        <span className={cn('tw-text-t-sm lg:tw-text-t-base', !user.active && 'tw-text-t-inactive')}>
                            {salutation}
                            <span className="tw-text-t-xs lg:tw-text-t-sm"> ({user.userId})</span>
                        </span>
                        {user.email && (
                            <a className={cn('tw-text-t-xs lg:tw-text-t-sm tw-text-primary', !user.active && 'tw-text-t-inactive')} href={`mailto:${user.email}`}>
                                {user.email}
                            </a>
                        )}
                    </div>
                    <FormField label={t('USER_GROUP.GROUPS')} htmlFor="user-groups-input">
                        <PrefixRankedCombobox
                            inputId="user-groups-input"
                            isMulti
                            options={groupOptions}
                            value={selectedGroupOptions}
                            placeholder={t('GRID.DEFAULT_OPTION')}
                            onChange={(value) => setSelectedGroupIds(optionValues(value))}
                        />
                    </FormField>
                    {effective.length > 0 && (
                        <FormField label={t('USER_GROUP.EFFECTIVE_ROLES')}>
                            <span className="tw-flex tw-flex-wrap tw-gap-1">
                                {effective.map((role) => (
                                    <Badge key={role} variant="neutral">{role}</Badge>
                                ))}
                            </span>
                        </FormField>
                    )}
                    <FormField label={t('USER_MANAGEMENT.ROLES')} htmlFor="user-roles-input">
                        <PrefixRankedCombobox
                            inputId="user-roles-input"
                            isMulti
                            options={roleOptions}
                            value={selectedRoleOptions}
                            placeholder={t('GRID.DEFAULT_OPTION')}
                            filterOption={roleFilterOption}
                            onChange={(value) => setRoles(optionValues(value))}
                        />
                    </FormField>
                    <div className="tw-flex tw-flex-col tw-gap-2">
                        <span className="tw-text-t-sm lg:tw-text-t-base tw-text-t-primary tw-text-left">{t('USER_MANAGEMENT.RESTRICTIONS')}</span>
                        <div className="tw-flex tw-flex-col tw-gap-5">
                            <FormField label={t('USER_MANAGEMENT.RESTRICTION_FILTER')} htmlFor="restriction-filter-input">
                                <PrefixRankedCombobox
                                    inputId="restriction-filter-input"
                                    isClearable
                                    options={filterOptions}
                                    value={selectedFilter}
                                    placeholder={t('GRID.DEFAULT_OPTION')}
                                    filterOption={restrictionFilterOption}
                                    formatOptionLabel={(option, meta) => filterOptionLabel(option as SelectOption, meta.context)}
                                    onChange={(value) => setRestrictionFilter((value as SelectOption | null)?.value ?? '')}
                                />
                            </FormField>
                            <FormField label={t('USER_MANAGEMENT.RESTRICTION_QUERY')} htmlFor="restriction-query-input">
                                <Input
                                    id="restriction-query-input"
                                    name="restrictionQuery"
                                    value={restrictionQuery}
                                    onChange={(event) => setRestrictionQuery(event.target.value)}
                                />
                            </FormField>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button id="update-user-btn" type="button" loading={updateUser.isPending} disabled={groupsQuery.isLoading} onClick={save}>
                        {t('SAVE')}
                    </Button>
                    <Button variant="secondary" type="button" className="btn-cancel" onClick={onClose}>
                        {t('CLOSE')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
